/* ==========================================================================
   BGC Hero slider — assets/bgc-hero.js
   Owned by sections/bgc-hero-slider.liquid.
   Auto-advancing crossfade slider. Small dots only (no arrows).
   Autoplay 6s/slide (configurable per instance via data-autoplay-ms),
   pause on hover/focus-within, resume on leave, dot click resets timer.
   GSAP + ScrollTrigger are loaded globally by theme.liquid; falls back to
   pure CSS opacity crossfade when GSAP is unavailable.
   Multiple section instances are safe — everything is scoped per root.
   ========================================================================== */

(function () {
  "use strict";

  var EASE = "power4.out";

  /* build <span class="hw">word</span> nodes via safe DOM methods (no innerHTML) */
  function splitWords(h1) {
    var words = h1.textContent.trim().split(/\s+/).filter(Boolean);
    if (!words.length) return;
    while (h1.firstChild) h1.removeChild(h1.firstChild);
    words.forEach(function (word, i) {
      var span = document.createElement("span");
      span.className = "hw";
      span.textContent = word;
      h1.appendChild(span);
      if (i < words.length - 1) h1.appendChild(document.createTextNode(" "));
    });
    h1.dataset.split = "1";
  }

  function init(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll("[data-hero-slide]"));
    var dots = Array.prototype.slice.call(root.querySelectorAll("[data-hero-dot]"));
    if (!slides.length) return;

    var hasGSAP = !!(window.gsap && window.ScrollTrigger);
    var prefersReduced =
      (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) ||
      document.documentElement.classList.contains("reduced");
    var animated = hasGSAP && !prefersReduced;

    var AUTOPLAY_MS = parseInt(root.getAttribute("data-autoplay-ms"), 10) || 6000;

    var current = Math.max(0, slides.findIndex(function (s) { return s.classList.contains("is-active"); }));
    if (current < 0) current = 0;
    var animating = false;
    var hovering = false;
    var autoplayTween = null;
    var autoplayTimeout = null;

    /* ---- split each slide's h1 into word spans (GSAP path only) ---- */
    if (animated) {
      slides.forEach(function (slide) {
        var h1 = slide.querySelector("[data-hero-heading]");
        if (!h1 || h1.dataset.split) return;
        splitWords(h1);
      });
    }

    function copyEls(slide) {
      return slide.querySelectorAll(".bgc-hero-copy .eyebrow, .bgc-hero-copy .lede, .bgc-hero-ctas");
    }
    function cylEls(slide) {
      return slide.querySelectorAll("[data-hero-cyl]");
    }

    function setControlTheme(idx) {
      root.classList.toggle("on-dark", slides[idx].classList.contains("is-dark"));
    }

    function revealSlide(slide, delay) {
      if (!animated) return;
      var words = slide.querySelectorAll(".hw");
      var cyls = cylEls(slide);
      var halo = slide.querySelector(".bgc-hero-halo");

      gsap.set(words, { opacity: 0, y: 26 });
      gsap.set(copyEls(slide), { opacity: 0, y: 18 });
      if (cyls.length) {
        gsap.set(cyls, {
          opacity: 0,
          x: function (i, target) {
            if (target.classList.contains("bgc-hero-cyl-left")) return -30;
            if (target.classList.contains("bgc-hero-cyl-right")) return 30;
            return 0;
          },
          y: 24
        });
      }
      if (halo) gsap.set(halo, { opacity: 0 });

      gsap.to(copyEls(slide), { opacity: 1, y: 0, duration: 0.6, ease: EASE, stagger: 0.08, delay: delay });
      gsap.to(words, { opacity: 1, y: 0, duration: 0.6, ease: EASE, stagger: 0.045, delay: delay + 0.12 });
      if (cyls.length) {
        gsap.to(cyls, { opacity: 1, x: 0, y: 0, duration: 0.8, ease: EASE, stagger: 0.06, delay: delay + 0.05 });
      }
      if (halo) gsap.to(halo, { opacity: 1, duration: 0.8, ease: EASE, delay: delay });
    }

    function hideSlide(slide) {
      if (!animated) return;
      var targets = [].concat(
        Array.prototype.slice.call(copyEls(slide)),
        Array.prototype.slice.call(slide.querySelectorAll(".hw"))
      );
      gsap.to(targets, { opacity: 0, y: -18, duration: 0.3, ease: "power2.in" });
      var cyls = cylEls(slide);
      if (cyls.length) gsap.to(cyls, { opacity: 0, y: -16, duration: 0.3, ease: "power2.in" });
      var halo = slide.querySelector(".bgc-hero-halo");
      if (halo) gsap.to(halo, { opacity: 0, duration: 0.3, ease: "power2.in" });
    }

    /* ---- autoplay: drives the active dot's pill fill (scaleX over AUTOPLAY_MS) ---- */
    function resetDotFills() {
      dots.forEach(function (d) {
        var fill = d.querySelector(".bgc-hero-dot-fill");
        if (!fill) return;
        if (animated) {
          gsap.set(fill, { scaleX: 0 });
        } else {
          fill.style.transition = "none";
          fill.style.transform = "scaleX(0)";
          // force reflow so the next transition re-triggers from 0
          // eslint-disable-next-line no-unused-expressions
          fill.offsetWidth;
          fill.style.transition = "";
        }
      });
    }

    function stopAutoplay() {
      if (autoplayTween) { autoplayTween.kill(); autoplayTween = null; }
      if (autoplayTimeout) { clearTimeout(autoplayTimeout); autoplayTimeout = null; }
    }

    function startAutoplay() {
      stopAutoplay();
      resetDotFills();
      var activeFill = dots[current] && dots[current].querySelector(".bgc-hero-dot-fill");

      if (animated && activeFill) {
        autoplayTween = gsap.to(activeFill, {
          scaleX: 1,
          duration: AUTOPLAY_MS / 1000,
          ease: "none",
          onComplete: function () { goTo(current + 1); }
        });
        if (hovering) autoplayTween.pause();
      } else {
        if (activeFill) {
          activeFill.style.transitionProperty = "transform";
          activeFill.style.transitionTimingFunction = "linear";
          activeFill.style.transitionDuration = AUTOPLAY_MS + "ms";
          // next frame: trigger the fill so the CSS transition animates 0 -> 1
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              activeFill.style.transform = "scaleX(1)";
            });
          });
        }
        if (!hovering) {
          autoplayTimeout = setTimeout(function () { goTo(current + 1); }, AUTOPLAY_MS);
        }
      }
    }

    function pauseAutoplay() {
      hovering = true;
      if (autoplayTween) autoplayTween.pause();
      if (autoplayTimeout) { clearTimeout(autoplayTimeout); autoplayTimeout = null; }
      var activeFill = dots[current] && dots[current].querySelector(".bgc-hero-dot-fill");
      if (!animated && activeFill) {
        // freeze the CSS transition at its current computed value
        var computed = getComputedStyle(activeFill).transform;
        activeFill.style.transitionDuration = "0ms";
        activeFill.style.transform = computed;
      }
    }

    function resumeAutoplay() {
      hovering = false;
      if (autoplayTween) {
        autoplayTween.resume();
      } else {
        startAutoplay();
      }
    }

    function goTo(idx) {
      idx = ((idx % slides.length) + slides.length) % slides.length;
      if (idx === current || animating) return;
      var prevSlide = slides[current];
      var nextSlide = slides[idx];
      animating = true;

      prevSlide.style.zIndex = 1;
      nextSlide.style.zIndex = 2;
      hideSlide(prevSlide);
      nextSlide.classList.add("is-active");
      setControlTheme(idx);
      revealSlide(nextSlide, animated ? 0.15 : 0);

      dots.forEach(function (d, i) {
        d.classList.toggle("is-on", i === idx);
        d.setAttribute("aria-selected", i === idx ? "true" : "false");
      });

      var delayMs = animated ? 320 : 0;
      setTimeout(function () {
        prevSlide.classList.remove("is-active");
        prevSlide.style.zIndex = "";
        nextSlide.style.zIndex = "";
        animating = false;
      }, delayMs);

      current = idx;
      startAutoplay(); // manual navigation (dot click) resets the timer
    }

    dots.forEach(function (d, i) {
      d.addEventListener("click", function () { goTo(i); });
    });

    root.addEventListener("mouseenter", pauseAutoplay);
    root.addEventListener("mouseleave", resumeAutoplay);
    root.addEventListener("focusin", pauseAutoplay);
    root.addEventListener("focusout", function (e) {
      if (!root.contains(e.relatedTarget)) resumeAutoplay();
    });

    setControlTheme(current);
    revealSlide(slides[current], animated ? 0.2 : 0);
    startAutoplay();
  }

  function boot() {
    var roots = document.querySelectorAll("[data-bgc-hero]");
    roots.forEach(function (root) { init(root); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* Re-init a specific instance if the theme editor swaps its blocks */
  document.addEventListener("shopify:section:load", function (evt) {
    var root = evt.target.querySelector && evt.target.querySelector("[data-bgc-hero]");
    if (root) init(root);
  });
})();
